/** New admin configuration section — separate from legacy Management flyout routes. */

export const VMS_ADMIN_CONFIG_PREFIX = '/vms/admin-config';

export const VMS_ADMIN_CONFIG_ROUTES = {
  annexureManagement: `${VMS_ADMIN_CONFIG_PREFIX}/annexure-management`,
  register: `${VMS_ADMIN_CONFIG_PREFIX}/register`,
  departmentCreation: `${VMS_ADMIN_CONFIG_PREFIX}/department-creation`,
  userRoleManagement: `${VMS_ADMIN_CONFIG_PREFIX}/user-role-management`,
  masterData: `${VMS_ADMIN_CONFIG_PREFIX}/master-data`,
} as const;

export type VmsAdminConfigNavItem = {
  name: string;
  href: string;
};

/** Full admin-configuration tab strip (VMS role `admin` only). */
export const VMS_ADMIN_CONFIG_NAV: VmsAdminConfigNavItem[] = [
  { name: 'Register', href: VMS_ADMIN_CONFIG_ROUTES.register },
  { name: 'Department Creation', href: VMS_ADMIN_CONFIG_ROUTES.departmentCreation },
  { name: 'User Role Management', href: VMS_ADMIN_CONFIG_ROUTES.userRoleManagement },
  { name: 'Master Data', href: VMS_ADMIN_CONFIG_ROUTES.masterData },
];

/** Register-only section tab for non-admin users on outsourcing register routes. */
export const VMS_REGISTER_SECTION_NAV: VmsAdminConfigNavItem[] = [
  { name: 'Register', href: VMS_ADMIN_CONFIG_ROUTES.register },
];

export function adminConfigRegisterDetailPath(registerRowId: string | number): string {
  return `${VMS_ADMIN_CONFIG_ROUTES.register}/${encodeURIComponent(String(registerRowId))}`;
}

export function adminConfigRegisterEditPath(registerRowId: string | number): string {
  return `${adminConfigRegisterDetailPath(registerRowId)}/edit`;
}

export function adminConfigRegisterAmendPath(registerRowId: string | number): string {
  return `${adminConfigRegisterDetailPath(registerRowId)}/amend`;
}

export function adminConfigRegisterHistoryPath(registerRowId: string | number): string {
  return `${adminConfigRegisterDetailPath(registerRowId)}/history`;
}

function pathMatches(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  const norm = href.replace(/\/$/, '');
  return pathname.startsWith(`${norm}/`);
}

export function isVmsAdminConfigNavItemActive(pathname: string, item: VmsAdminConfigNavItem): boolean {
  return pathMatches(pathname, item.href);
}

export function isVmsAdminConfigNavPath(pathname: string): boolean {
  return pathname === VMS_ADMIN_CONFIG_PREFIX || pathname.startsWith(`${VMS_ADMIN_CONFIG_PREFIX}/`);
}

/** Outsourcing register (list, view) — any authenticated VMS user; not governance-only admin config. */
export function isVmsOutsourcingRegisterPath(pathname: string): boolean {
  return pathMatches(pathname, VMS_ADMIN_CONFIG_ROUTES.register);
}

/** Admin-config routes that require VMS role `admin` (excludes outsourcing register). */
export function isVmsAdminConfigGovernancePath(pathname: string): boolean {
  if (isVmsOutsourcingRegisterPath(pathname)) return false;
  return isVmsAdminConfigNavPath(pathname);
}

export function isLegacyOutsourcingVendorsPath(pathname: string): boolean {
  return pathname === '/vms/outsourcing-vendors' || /^\/vms\/outsourcing-vendors\/\d+$/.test(pathname);
}

export function isOutsourcingRegisterPath(pathname: string): boolean {
  return isVmsOutsourcingRegisterPath(pathname) || isLegacyOutsourcingVendorsPath(pathname);
}
